/**
 * Vendas Canopus feitas pelo app (canopus_vendas_app, migration 248): quem
 * vendeu, para qual contato, e o status do pedido no AFV (canopus_vendas, pelo
 * id_pedido). Rascunho ou pendência pode ser enviado daqui — é a venda real,
 * então passa por uma confirmação.
 */
import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { useGetList, useNotify, useRefresh } from 'ra-core';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Spinner } from '@/components/ui/spinner';
import { supabase } from '@/components/atomic-crm/providers/supabase/supabase';
import { baixarDocumentoCanopus, enviarVendaCanopus, type DocumentoCanopus } from '@/lib/canopus-api';
import { formatBRL } from '@/lib/format';
import { CaixaTabela } from '@/components/ui/table';
import { Card } from '@/components/ui/card';
import { CabecalhoPagina } from '../bento/ui';

const dataHora = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '—';

export type VendaApp = {
  id: number; id_pedido: number; contact_id: number | null; sales_person_id: number | null; login_afv: string | null;
  nome_cliente: string | null; plano_codigo: string | null; valor_credito: number | null; prazo: number | null;
  grupo: string | null; primeira_parcela: number | null; status: 'rascunho' | 'enviada' | 'pendencia' | 'erro';
  pendencia: string | null; enviada_em: string | null; created_at: string;
};

export const STATUS_APP: Record<VendaApp['status'], string> = {
  rascunho: 'Rascunho (não enviada)',
  enviada: 'Enviada',
  pendencia: 'Pendência',
  erro: 'Interrompida',
};

/** Rascunho e pendência podem ser enviados; enviada não; erro só depois de conferir no AFV. */
export const podeEnviar = (v: Pick<VendaApp, 'status'>) => v.status === 'rascunho' || v.status === 'pendencia';

export function CanopusVendasApp() {
  const notify = useNotify();
  const refresh = useRefresh();
  const [enviar, setEnviar] = useState<VendaApp | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [andamento, setAndamento] = useState('');

  const { data, isPending, error } = useGetList<VendaApp>('canopus_vendas_app', {
    pagination: { page: 1, perPage: 500 },
    sort: { field: 'created_at', order: 'DESC' },
  });
  const { data: afv } = useGetList<{ id: number; id_pedido: number | null; status: string | null; id_contrato: string | null }>('canopus_vendas', {
    pagination: { page: 1, perPage: 1000 },
    sort: { field: 'id', order: 'DESC' },
  });
  const { data: usuarios } = useGetList<{ id: number; name?: string | null }>('users', {
    pagination: { page: 1, perPage: 1000 },
    sort: { field: 'name', order: 'ASC' },
  });

  const noAfv = useMemo(() => new Map((afv || []).map((v) => [Number(v.id_pedido), v])), [afv]);
  const nomes = useMemo(() => new Map((usuarios || []).map((u) => [Number(u.id), u.name || `Usuário ${u.id}`])), [usuarios]);

  const [baixando, setBaixando] = useState('');
  const baixar = async (v: VendaApp, tipo: DocumentoCanopus) => {
    setBaixando(`${v.id_pedido}-${tipo}`);
    try {
      const { data: sessao } = await supabase.auth.getSession();
      const token = sessao.session?.access_token;
      if (!token) throw new Error('Sessão expirada. Entre novamente no sistema.');
      const arquivo = await baixarDocumentoCanopus(v.id_pedido, tipo, token);
      const url = URL.createObjectURL(arquivo);
      const a = document.createElement('a');
      a.href = url; a.download = `canopus-${tipo}-${v.id_pedido}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e: any) {
      notify(e?.message || `Não foi possível baixar o ${tipo}.`, { type: 'error' });
    } finally {
      setBaixando('');
    }
  };

  const confirmar = async () => {
    if (!enviar) return;
    setEnviando(true); setAndamento('Enviando para o servidor…');
    try {
      const { data: sessao } = await supabase.auth.getSession();
      const token = sessao.session?.access_token;
      if (!token) throw new Error('Sessão expirada. Entre novamente no sistema.');
      const r = await enviarVendaCanopus(enviar.id_pedido, token, setAndamento);
      notify(r.finalizado ? `Pedido ${r.id_pedido} enviado à Canopus.` : `A Canopus apontou pendência: ${r.pendencia}`, { type: r.finalizado ? 'success' : 'warning' });
      setEnviar(null);
      refresh();
    } catch (e: any) {
      notify(e?.message || 'Não foi possível enviar à Canopus.', { type: 'error' });
    } finally {
      setEnviando(false); setAndamento('');
    }
  };

  return (
    <section className="space-y-4">
      <CabecalhoPagina titulo="Vendas Canopus pelo app" descricao="Vendedor, contato e situação do pedido no AFV." />
      <Card className="gap-0 overflow-hidden py-0">
      {isPending ? (
        <p className="flex items-center gap-2 px-4 py-6 text-sm"><Spinner className="h-4 w-4" />Carregando…</p>
      ) : error ? (
        <p className="px-4 py-6 text-sm text-erro">Não foi possível carregar as vendas pelo app.</p>
      ) : !data?.length ? (
        <p className="px-4 py-10 text-center text-sm text-muted-foreground">Nenhuma venda Canopus pelo app.</p>
      ) : (
        <CaixaTabela>
          <table className="w-full text-sm">
            <thead className="text-left text-xs text-muted-foreground [&_th]:h-10 [&_th]:font-medium">
              <tr className="border-b border-border">
                <th className="px-3">Quando</th>
                <th className="px-3">Pedido</th>
                <th className="px-3">Cliente</th>
                <th className="px-3">Vendedor</th>
                <th className="px-3 text-right">Crédito</th>
                <th className="px-3">Status</th>
                <th className="px-3">No AFV</th>
                <th className="px-3" />
              </tr>
            </thead>
            <tbody>
              {data.map((v) => {
                const a = noAfv.get(Number(v.id_pedido));
                return (
                  <tr key={v.id} className="border-b border-border/70 align-top last:border-b-0">
                    <td className="px-3 py-2.5 tabular-nums">{dataHora(v.created_at)}</td>
                    <td className="px-3 py-2.5 font-mono text-xs">{v.id_pedido}{v.grupo ? <span className="block text-xs text-muted-foreground">grupo {v.grupo}</span> : null}</td>
                    <td className="px-3 py-2.5">
                      {v.contact_id ? <Link className="underline" to={`/contacts/${v.contact_id}/show`}>{v.nome_cliente || `Contato ${v.contact_id}`}</Link> : v.nome_cliente || '—'}
                    </td>
                    <td className="px-3 py-2.5">{v.sales_person_id ? nomes.get(Number(v.sales_person_id)) ?? `Usuário ${v.sales_person_id}` : '—'}</td>
                    <td className="px-3 py-2.5 text-right">{v.valor_credito != null ? formatBRL(Number(v.valor_credito)) : '—'}</td>
                    <td className="px-3 py-2.5">
                      {STATUS_APP[v.status] ?? v.status}
                      {v.pendencia ? <span className="block text-xs text-alerta">{v.pendencia}</span> : null}
                    </td>
                    <td className="px-3 py-2.5">{a ? `${a.status || '—'}${a.id_contrato ? ` · contrato ${a.id_contrato}` : ''}` : <span className="text-muted-foreground">não está no último sync</span>}</td>
                    <td className="px-3 py-2.5 text-right">
                      <div className="flex flex-wrap justify-end gap-1">
                        {podeEnviar(v) && <Button size="sm" variant="outline" onClick={() => setEnviar(v)}>Enviar à Canopus</Button>}
                        {a?.id_contrato && (['boleto', 'proposta'] as const).map((tipo) => (
                          <Button key={tipo} size="sm" variant="ghost" disabled={!!baixando} onClick={() => baixar(v, tipo)}>
                            {baixando === `${v.id_pedido}-${tipo}` ? 'Baixando…' : tipo === 'boleto' ? 'Boleto' : 'Proposta'}
                          </Button>
                        ))}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </CaixaTabela>
      )}
      </Card>

      <Dialog open={!!enviar} onOpenChange={(aberto) => !aberto && !enviando && setEnviar(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Enviar o pedido {enviar?.id_pedido} à Canopus?</DialogTitle>
            <DialogDescription>
              A Canopus registra a proposta e o cliente {enviar?.nome_cliente} recebe contrato e boleto por e-mail. Esta ação não pode ser desfeita pelo app.
            </DialogDescription>
          </DialogHeader>
          {andamento && <p className="flex items-center gap-2 text-sm"><Spinner className="h-4 w-4" />{andamento}</p>}
          <DialogFooter>
            <Button variant="outline" disabled={enviando} onClick={() => setEnviar(null)}>Cancelar</Button>
            <Button disabled={enviando} onClick={confirmar}>{enviando ? 'Enviando…' : 'Sim, enviar à Canopus'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
